import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/config/site";
import { Wordmark } from "@/components/Navbar";
import { LogOut, RefreshCw, Inbox, Trash2, Download } from "lucide-react";

const FIELD_LABELS = [
  ["phone", "Phone"], ["industry", "Industry"], ["employees", "Employees"], ["website", "Website"],
  ["current_systems", "Current systems"], ["management_time", "Takes up too much management time"],
  ["manual_processes", "Processes still manual"], ["improve_most", "Would most like to improve"],
  ["additional_info", "Additional information"],
];

export default function AdminEnquiries() {
  const [state, setState] = useState({ status: "loading", enquiries: [], error: "" });
  const navigate = useNavigate();

  const load = async () => {
    try {
      await axios.get(`${API}/auth/me`, { withCredentials: true });
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setState({ status: "ready", enquiries: data, error: "" });
    } catch {
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    await axios.delete(`${API}/enquiries/${id}`, { withCredentials: true });
    setState((s) => ({ ...s, enquiries: s.enquiries.filter((e) => e.id !== id) }));
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/enquiries/export`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "westline-enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-offwhite" data-testid="admin-enquiries-page">
      <header className="bg-charcoal border-b border-[#2A2D2A]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Wordmark light />
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 text-[13px] font-medium text-zinc-400 hover:text-white border border-[#2A2D2A] rounded-md px-3.5 py-2 transition-colors"
              data-testid="admin-export-csv-button"
            >
              <Download size={13} /> Export CSV
            </button>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 text-[13px] font-medium text-zinc-400 hover:text-white border border-[#2A2D2A] rounded-md px-3.5 py-2 transition-colors"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-[13px] font-medium text-zinc-400 hover:text-white border border-[#2A2D2A] rounded-md px-3.5 py-2 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-charcoal text-2xl tracking-tight" data-testid="admin-enquiries-heading">
              Website Enquiries
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Private area — submissions from the website enquiry form. Not publicly accessible.
            </p>
          </div>
          <Link to="/" className="text-[13px] text-zinc-500 hover:text-forest transition-colors" data-testid="admin-back-to-site-link">
            ← Back to website
          </Link>
        </div>

        {state.status === "loading" && (
          <p className="mt-10 text-sm text-zinc-500" data-testid="admin-loading">Loading enquiries…</p>
        )}

        {state.status === "ready" && state.enquiries.length === 0 && (
          <div className="mt-10 bg-white border border-gray-200 rounded-lg p-12 text-center" data-testid="admin-empty-state">
            <Inbox size={30} className="mx-auto text-zinc-300" strokeWidth={1.5} />
            <p className="mt-4 font-heading font-semibold text-charcoal">No enquiries yet</p>
            <p className="mt-1 text-sm text-zinc-500">New submissions from the website form will appear here.</p>
          </div>
        )}

        <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
          {state.enquiries.map((enq) => (
            <article key={enq.id} className="bg-white border border-gray-200 rounded-lg p-6" data-testid={`admin-enquiry-card-${enq.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-bold text-charcoal text-base">
                    {enq.name}
                    {enq.business_name && <span className="text-zinc-500 font-medium"> — {enq.business_name}</span>}
                  </p>
                  <a href={`mailto:${enq.email}`} className="text-sm text-forest hover:underline">{enq.email}</a>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="text-[12px] text-zinc-500">
                    {new Date(enq.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                  <button
                    onClick={() => remove(enq.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    aria-label="Delete enquiry"
                    data-testid={`admin-delete-enquiry-${enq.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <dl className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {FIELD_LABELS.filter(([k]) => enq[k]).map(([k, label]) => (
                  <div key={k}>
                    <dt className="text-[11px] font-semibold tracking-[0.14em] uppercase text-zinc-400">{label}</dt>
                    <dd className="mt-0.5 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{enq[k]}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
